/**
 * Home — kontots översikt: hälsning, nästa steg, summering, senaste händelser.
 *
 * Visade tidigare en fast MOCK: "Hej Anna", integritetspoäng 72,
 * "6 öppna exponeringar", "12 borttagna", och en händelselista med
 * MrKoll/Ratsit/Merinfo/Eniro. Ingenting av det kom från kontot — samma
 * siffror för varje inloggad kund. Nu härleds allt ur /v1/me och /v1/tickets,
 * och saknas data står det att den saknas.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusPill, type RemovalStatus } from '../components/StatusPill';
import { fetchMe, listTickets, type MeResponse, type Ticket } from '../lib/api';
import './home.css';

const KNOWN: RemovalStatus[] = [
  'DISCOVERED',
  'QUEUED',
  'SUBMITTED',
  'ACKNOWLEDGED',
  'REMOVED',
  'DENIED',
  'NEEDS_USER_ACTION',
  'REAPPEARED',
];

function asStatus(s: string): RemovalStatus {
  if ((KNOWN as string[]).includes(s)) return s as RemovalStatus;
  const alias: Record<string, RemovalStatus> = {
    sent: 'SUBMITTED',
    draft: 'QUEUED',
    found: 'DISCOVERED',
    responded: 'ACKNOWLEDGED',
    denied: 'DENIED',
    done: 'REMOVED',
  };
  return alias[s] ?? 'QUEUED';
}

const day = (v: unknown) => {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Date(n).toISOString().slice(0, 10);
};

export default function Home() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [meRes, tRes] = await Promise.all([fetchMe(), listTickets()]);
        if (!alive) return;
        setMe(meRes.data);
        setTickets(tRes.data ?? []);
      } catch (e) {
        const msg = e instanceof Error ? e.message : '';
        if (alive) {
          setError(
            msg === 'unauthorized'
              ? 'Du är utloggad — logga in för att se din översikt.'
              : 'Kunde inte hämta din översikt.',
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const firstName = (me?.name || me?.email?.split('@')[0] || '').split(' ')[0];
  const done = ['REMOVED', 'done'];
  const closed = [...done, 'DENIED', 'denied'];

  const summary = [
    {
      value: tickets.filter((t) => !closed.includes(t.status)).length,
      label: 'Öppna ärenden',
      tone: 'navy' as const,
    },
    {
      value: tickets.filter((t) => done.includes(t.status)).length,
      label: 'Borttagna',
      tone: 'green' as const,
    },
    { value: tickets.length, label: 'Totalt', tone: 'navy' as const },
    {
      value: tickets.filter((t) => t.status === 'REAPPEARED').length,
      label: 'Återkomna',
      tone: 'danger' as const,
      highlight: tickets.some((t) => t.status === 'REAPPEARED'),
    },
  ];

  const needsAction = tickets.filter((t) => t.status === 'NEEDS_USER_ACTION').length;

  const events = tickets
    .flatMap((t) =>
      ((t.timeline ?? []) as { at: unknown; status: string; note?: string }[]).map((e) => ({
        at: Number(e.at) || 0,
        text: `${t.sourceName ?? t.caseId} — ${e.note ?? e.status}`,
        status: asStatus(e.status),
      })),
    )
    .sort((a, b) => b.at - a.at)
    .slice(0, 6);

  return (
    <div className="home-page">
      <header className="home-header">
        <h1 className="home-greeting">{firstName ? `Hej, ${firstName}` : 'Hej'}</h1>
        <span className="home-next-scan">
          {me?.entitlement?.canRemove ? 'Skydd aktivt' : 'Gratis — scan ingår'}
        </span>
      </header>

      {loading && <p className="home-score-hint">Hämtar…</p>}
      {error && <p className="home-score-hint">{error}</p>}

      {!loading && !error && (
        <>
          <div className="home-hero-grid">
            <section className="home-card home-next">
              <div className="home-next-body">
                <div className="home-eyebrow">Nästa steg</div>
                <div className="home-next-title">
                  {tickets.length === 0
                    ? 'Kör din första scan'
                    : needsAction > 0
                      ? `${needsAction} ${needsAction === 1 ? 'ärende kräver' : 'ärenden kräver'} din åtgärd`
                      : 'Inget väntar på dig just nu'}
                </div>
                <p className="home-next-hint">
                  {tickets.length === 0
                    ? 'Scanen är gratis. Du ser vad som finns om dig innan du bestämmer dig.'
                    : 'Öppna ärendena för att se status och bevis.'}
                </p>
              </div>
              <Link className="home-btn" to={tickets.length === 0 ? '/find' : '/remove'}>
                {tickets.length === 0 ? 'Kör scan →' : 'Öppna ärenden →'}
              </Link>
            </section>
          </div>

          {tickets.length > 0 && (
            <div className="home-summary-grid">
              {summary.map((s) => (
                <div
                  key={s.label}
                  className={`home-card home-stat${s.highlight ? ' is-highlight' : ''}`}
                >
                  <div className={`home-stat-value tone-${s.tone}`}>{s.value}</div>
                  <div className="home-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          <section className="home-card home-events" aria-label="Senaste händelser">
            {events.length === 0 ? (
              <p className="home-score-hint">
                Inga händelser ännu. Kör en scan för att komma igång.
              </p>
            ) : (
              <ul className="home-event-list">
                {events.map((ev, i) => (
                  <li key={i} className="home-event">
                    <time className="home-event-date">{day(ev.at)}</time>
                    <span className="home-event-text">{ev.text}</span>
                    <StatusPill status={ev.status} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
